/**
 * useSiweAuth - Wallet connect hook (read-only build)
 *
 * The read-only site has no backend auth. This hook now only captures the
 * connected wallet address from the injected provider and persists it locally;
 * there is no nonce, no signature, and no JWT. The hook signature and the
 * onSuccess({ address }) contract are unchanged so callers keep working.
 */

import { useState, useCallback } from 'react';

/**
 * Hook for wallet connect with MetaMask
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback when connect succeeds
 * @param {Function} options.onError - Callback when connect fails
 * @param {string} options.statement - Unused (kept for signature compatibility)
 * @returns {Object} - { handleSignIn, isSigningMessage }
 */
export const useSiweAuth = ({ onSuccess, onError, statement } = {}) => {
  const [isSigningMessage, setIsSigningMessage] = useState(false);

  const handleSignIn = useCallback(async () => {
    setIsSigningMessage(true);

    try {
      // Prefer injected provider
      const provider = typeof window !== 'undefined' ? window.ethereum : null;

      if (!provider) {
        // On mobile Safari/Chrome without injection, direct user to MetaMask in-app browser
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
        if (isMobile) {
          const origin = typeof window !== 'undefined' ? window.location.origin : '';
          const path = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';
          // Require https to avoid "Proceed with caution". If dev uses http, advise switching to an https tunnel.
          const httpsUrl = origin.replace('http://', 'https://') + path;
          const deeplink = `https://metamask.app.link/dapp/${httpsUrl.replace(/^https?:\/\//, '')}`;
          setIsSigningMessage(false);
          window.location.href = deeplink;
          return;
        }
        const error = new Error('MetaMask not detected');
        error.code = 'NO_PROVIDER';
        throw error;
      }

      // Connect to get accounts (no signature step in the read-only build)
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts connected');
      }
      const addr = accounts[0].toLowerCase();

      // Persist the address locally so profile/referral views can render
      localStorage.setItem('wallet_address', addr);

      if (onSuccess) {
        onSuccess({ address: addr });
      }

      return { address: addr };
    } catch (error) {
      console.error('[useSiweAuth] Connect failed:', error);

      // Handle user rejection
      if (error && error.code === 4001) {
        error.message = 'User rejected the connection request';
      }

      if (onError) {
        onError(error);
      }

      throw error;
    } finally {
      setIsSigningMessage(false);
    }
  }, [onSuccess, onError, statement]);

  return {
    handleSignIn,
    isSigningMessage,
  };
};

export default useSiweAuth;
